import React, { ReactNode } from "react";
import { Button, Grid } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import { FileWithPath } from "react-dropzone";

import { APIError } from "src/types";
import { Dropzone } from "src/components/Dropzone";
import { DropzoneHeader } from "src/components/DropzoneHeader";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useTrafficEmissionImportSchema, SUPPORTED_FORMATS } from "src/schemas/traffic-emission-import.schema";
import { fieldToError } from "src/utils/form.utils";
import { DropzoneSingleFileRequirementError } from "src/errors";
import { useTemplates } from "src/hooks";

export type TrafficEmissionImportFormData = {
  segmentFile: File | null;
};

interface Props {
  data: TrafficEmissionImportFormData;
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (values: TrafficEmissionImportFormData) => void;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<TrafficEmissionImportFormData> | null>;
  statusBody: ReactNode;
}

export function TrafficEmissionImportForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = useTrafficEmissionImportSchema();
  const { data: templates } = useTemplates();

  const initialValues: TrafficEmissionImportFormData = {
    segmentFile: props.data.segmentFile,
  };

  return render();

  function render() {
    if (!templates) return null;

    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          return props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ isSubmitting, isValid, dirty, setFieldValue, errors }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <DropzoneHeader label={t("label.segmentFile")} template={templates.networkTemplateUrl} />
                <Dropzone
                  dropzoneOptions={{
                    onDrop: (acceptedFiles: FileWithPath[]) => {
                      if (acceptedFiles.length > 1) throw DropzoneSingleFileRequirementError();

                      setFieldValue("segmentFile", acceptedFiles[0]);
                    },
                    accept: SUPPORTED_FORMATS,
                    multiple: false,
                  }}
                />

                {errors.segmentFile && (
                  <Grid item xs={12}>
                    <ServerErrors data={[fieldToError("segmentFileError", errors.segmentFile)]} />
                  </Grid>
                )}
              </Grid>

              {props.statusBody}

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              {!props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.close")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!(dirty && isValid)}
                  >
                    {t("label.upload")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
